import { PostgrestError, SupabaseClient } from "@supabase/supabase-js";

import { Database } from "../../supabase.types";
import { getCDNImageURL } from "@punchup/utils/getCDNImageURL";
import { isHexColor } from "@punchup/utils/colors";
import { logException, logSupabaseException } from "@punchup/sentry";

/**
 * The venue identity a report's artist statement prints in its header. Mirrors
 * the Go `ReportConfig.branding` block — `logo_url` is a fully-formed image
 * URL, never the stored `venue_pages.logo_img` (a Cloudflare Images id).
 */
export interface ReportVenueBranding {
  venue_name: string;
  logo_url: string;
  /** The colour the logo prints on, `#RGB` or `#RRGGBB`; absent when the venue page's theme could not be read or held another shape. */
  logo_background?: string;
}

/** Sync: the `logo_background_color` `getVenuePageThemeWithFallback` (`@punchup/venues`) paints for a page with no theme row. */
const DEFAULT_LOGO_BACKGROUND = "#000000";

const UNBRANDED = "[report-branding] the artist statement enqueues unbranded";
const NO_LOGO_BACKGROUND =
  "[report-branding] the artist statement enqueues without its logo background";

/** Which venue a statement's branding was asked for, in the id space its
 *  caller named it by — every log line below carries it. */
type BrandingSubject = { tixologiPartnerId: number } | { venuePageId: string };

function warnUnbranded(subject: BrandingSubject, reason: string): null {
  console.warn(`${UNBRANDED}: ${reason}`, subject);
  return null;
}

async function storedLogoBackground(
  supabase: SupabaseClient<Database>,
  venuePageSlug: string
): Promise<string> {
  const { data: theme, error } = await supabase
    .from("venue_pages_themes")
    .select("logo_background_color")
    .eq("venue_page_slug", venuePageSlug)
    .order("id", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return theme?.logo_background_color ?? DEFAULT_LOGO_BACKGROUND;
}

function reportUnreadLogoBackground(
  failure: unknown,
  subject: BrandingSubject
): undefined {
  const what = `${NO_LOGO_BACKGROUND}: venue_pages_themes read failed`;
  if (failure instanceof Error) {
    logException(failure, { data: what, extra: subject });
  } else {
    logSupabaseException(failure as PostgrestError, what, { extra: subject });
  }
  return undefined;
}

async function logoBackgroundOf(
  supabase: SupabaseClient<Database>,
  venuePageSlug: string,
  subject: BrandingSubject
): Promise<string | undefined> {
  const stored = await storedLogoBackground(supabase, venuePageSlug).catch(
    (failure: unknown) => reportUnreadLogoBackground(failure, subject)
  );

  if (stored === undefined) return undefined;
  if (isHexColor(stored)) return stored;
  console.warn(
    `${NO_LOGO_BACKGROUND}: the logo_background_color of venue page ${venuePageSlug} is not #RGB or #RRGGBB`,
    subject
  );
  return undefined;
}

async function brandingOfVenuePage(
  supabase: SupabaseClient<Database>,
  venuePageId: string,
  subject: BrandingSubject
): Promise<ReportVenueBranding | null> {
  const { data: page, error: pageError } = await supabase
    .from("venue_pages")
    .select("name, logo_img, slug")
    .eq("id", venuePageId)
    .limit(1)
    .maybeSingle();

  if (pageError) {
    logSupabaseException(pageError, `${UNBRANDED}: venue_pages read failed`, {
      extra: subject,
    });
    return null;
  }
  if (!page?.name) {
    return warnUnbranded(
      subject,
      `venue page ${venuePageId} is missing or has no name`
    );
  }

  const branding: ReportVenueBranding = {
    venue_name: page.name,
    logo_url: page.logo_img ? getCDNImageURL(page.logo_img) : "",
  };
  const logoBackground = await logoBackgroundOf(supabase, page.slug, subject);
  return logoBackground
    ? { ...branding, logo_background: logoBackground }
    : branding;
}

/** The media types the artist statement prints a logo from. */
const PRINTED_LOGO_MEDIA_TYPES = new Set(["image/png", "image/jpeg"]);
/**
 * The Accept header the statement's own logo fetch sends (the ticketing-api's
 * `statementLogoAccept`): the two types it prints and nothing else. Sent here
 * so the CDN answers this question as it answers the statement, whatever
 * format it would negotiate for another client.
 */
const STATEMENT_LOGO_ACCEPT = "image/png, image/jpeg";
/** A page waits on the answer, so a slow CDN costs the warning, not the page. */
const LOGO_TYPE_TIMEOUT_MS = 3000;
const LOGO_TYPE_UNREAD =
  "Venue logo media type unread, so no print warning shows";

/**
 * Whether a venue's stored logo prints on its reports. The artist statement
 * prints a logo only from a PNG or JPEG; for any other type, an SVG included,
 * it prints the venue's name alone. The stored value is a Cloudflare Images id
 * with no extension, and the upload records no media type, so this asks the
 * CDN which media type it serves at the address the statement fetches the logo
 * from: true for a PNG or JPEG, false for any other type.
 *
 * Null with no logo, and whenever the answer cannot be read, so no warning
 * shows on a guess. An unread answer is logged, since a CDN that stopped
 * answering would otherwise hide the warning without a trace.
 */
export async function venueLogoPrintsOnReports(
  logoImageId: string | null | undefined
): Promise<boolean | null> {
  if (!logoImageId) return null;
  try {
    const response = await fetch(getCDNImageURL(logoImageId), {
      method: "HEAD",
      headers: { Accept: STATEMENT_LOGO_ACCEPT },
      signal: AbortSignal.timeout(LOGO_TYPE_TIMEOUT_MS),
    });
    if (!response.ok) {
      console.warn(`${LOGO_TYPE_UNREAD}: the CDN answered ${response.status}`, {
        logoImageId,
      });
      return null;
    }
    const mediaType = response.headers
      .get("content-type")
      ?.split(";")[0]
      .trim()
      .toLowerCase();
    if (!mediaType) {
      console.warn(`${LOGO_TYPE_UNREAD}: the CDN named no media type`, {
        logoImageId,
      });
      return null;
    }
    return PRINTED_LOGO_MEDIA_TYPES.has(mediaType);
  } catch (error) {
    console.warn(
      `${LOGO_TYPE_UNREAD}: ${error instanceof Error ? error.message : String(error)}`,
      { logoImageId }
    );
    return null;
  }
}

/**
 * Resolve a venue page's own branding for a report job — the venue door's
 * resolver, keyed on the venue page the caller was authorised for, so the
 * header can never come from a stale `ticketing_partners` mapping.
 *
 * Returns null for every unresolvable case and logs which, with the venue
 * page id, exactly as its ticketing-venue twin below does.
 */
export async function getReportBrandingForVenuePage(
  supabase: SupabaseClient<Database>,
  venuePageId: string
): Promise<ReportVenueBranding | null> {
  const subject = { venuePageId };
  try {
    return await brandingOfVenuePage(supabase, venuePageId, subject);
  } catch (error) {
    logException(error, { extra: subject });
    return null;
  }
}

/**
 * Resolve a ticketing venue's Punchup branding for a report job — crossing the
 * id space `getTicketingPartner` crosses in the other direction
 * (`ticketing_partners.tixologi_partner_id` → that row's `id`, which IS the
 * `venue_pages.id`).
 *
 * Returns null for EVERY unresolvable case, so a caller enqueues without
 * branding rather than refusing a report over a header — and logs which case
 * it was, with the partner id. A venue with no logo is a different answer: its
 * name, and an empty `logo_url`.
 */
export async function getReportBrandingForTicketingVenue(
  supabase: SupabaseClient<Database>,
  tixologiPartnerId: number
): Promise<ReportVenueBranding | null> {
  const subject = { tixologiPartnerId };
  const context = { extra: subject };
  try {
    const { data: partner, error: partnerError } = await supabase
      .from("ticketing_partners")
      .select("id")
      .eq("tixologi_partner_id", String(tixologiPartnerId))
      .limit(1)
      .maybeSingle();

    if (partnerError) {
      logSupabaseException(
        partnerError,
        `${UNBRANDED}: ticketing_partners read failed`,
        context
      );
      return null;
    }
    if (!partner?.id) {
      return warnUnbranded(
        subject,
        "no ticketing_partners row carries this tixologi_partner_id"
      );
    }

    return await brandingOfVenuePage(supabase, partner.id, subject);
  } catch (error) {
    logException(error, context);
    return null;
  }
}
